/**
 * @fileoverview Company Tags Item Component
 * @active Used in current Next.js App Router routes
 * 
 * This component renders a single tag with custom styling based on the tag's color.
 * Used to display categorization tags for companies (e.g., industry, technology, etc.)
 * It is part of the current active implementation for the companies card view.
 * 
 * @see {@link src/components/companies/newCompaniesCards/companyNewCardItem/CompanyCardItemHeader.tsx} - Parent component
 * @see {@link src/components/companies/newCompaniesCards/companyNewCardItem/index.tsx} - Card item component
 * @see {@link src/components/companies/newCompaniesCards/index.tsx} - Card view container
 * @see {@link src/components/companies/NewCompanies.tsx} - Main active implementation
 * @see {@link src/components/companies/CompaniesWrapper.client.tsx} - Client wrapper used in App Router
 * @see {@link src/app/(main)/(home)/(research)/companies/page.tsx} - Current App Router page component
 */

import React, { memo } from "react";

import { Flex, Text } from "@chakra-ui/react";

/**
 * Interface for tag information
 */
interface TagInfo {
  name: string;
  color: string;
}

/**
 * Interface for the CompanyTagsItem props
 */
interface CompanyTagsItemProps {
  tag: TagInfo;
}

/**
 * Company Tags Item component
 * 
 * Displays a single tag with custom styling based on the tag's color.
 * 
 * @param props - Component props
 * @returns Rendered component
 */
const CompanyTagsItem: React.FC<CompanyTagsItemProps> = ({ tag }) => {
  return (
    <Flex
      align={"center"}
      justify={"center"}
      p={"4px"}
      borderRadius={"4px"}
      bg={tag?.color}
    >
      <Text
        mt={"4px"}
        fontSize={"14px"}
        fontWeight={"400"}
        lineHeight={"1"}
        letterSpacing={"2%"}
      >
        {tag?.name}
      </Text>
    </Flex>
  );
};

export default memo(CompanyTagsItem);
